import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ChoiceQuestionPayload,
  DigitalCase,
  DigitalQuestion,
  QuestionChoiceWithText,
  QuestionMultiChoice,
  QuestionMultiChoiceWithText,
  QuestionNumberRange,
  QuestionRatio,
  QuestionSingleChoice,
  QuestionSingleChoiceWithText,
  QuestionString,
  QuestionTypeV2,
  RootState,
} from '../../redux/types';

const SectionGroup = styled(Box)`
  display: block;
  margin: 1px;
  width: 210mm;
`;

const SectionContainer = styled(Box)`
  margin-top: 16px;

  table.multiple-choice {
    table-layout: fixed;
    border: 1px solid rgba(224, 224, 224, 1);
    th{
      text-align: center;
      padding: 0;
      max-height: 1.5rem;
    }
    td:first-of-type,
    th:first-of-type {
      width: 250px;
      padding: 6px 16px 6px 1px;
      font-weight: bold;
    }
  }

  td, th {
    border: 1px solid rgba(224, 224, 224, 1);
    font-size: 0.8rem;
  }

  td:nth-of-type(n+2) {
    text-align: center;
  }
  &:nth-of-type(n+2){
    break-before: page;
    break-before: column;
    page-break-before: always;
  }
`;

type Props = {
  onRef: any;
}

const WholeSurveyPrint: React.FC<Props> = ({ onRef }: Props) => {
  const sectionsAllIds = useSelector((state: RootState) => state.SurveyData.digitalSections.allIds);
  const sectionsById = useSelector((state: RootState) => state.SurveyData.digitalSections.byId);
  const questionsAllIds = useSelector(
    (state: RootState) => state.SurveyData.digitalQuestions.allIds,
  );
  const questionsById = useSelector((state: RootState) => state.SurveyData.digitalQuestions.byId);
  const casesAllIds = useSelector((state: RootState) => state.SurveyData.digitalCases.allIds);
  const casesById = useSelector((state: RootState) => state.SurveyData.digitalCases.byId);

  const sections = sectionsAllIds.map((i) => sectionsById[i]);
  const getQuestions = (secId: number) => questionsAllIds.map((i) => questionsById[i])
    .filter((q) => q.digitalSectionId === secId && !q.isContextQuestion);
  const getContextQuestions = (secId: number) => questionsAllIds.map((i) => questionsById[i])
    .filter((q) => q.digitalSectionId === secId && q.isContextQuestion);
  const getCases = (secId: number) => casesAllIds.map((c) => casesById[c])
    .filter((c) => c.digitalSectionId === secId);

  if (!questionsAllIds.length || !casesAllIds.length || !sectionsAllIds.length) {
    return null;
  }

  const renderRowsForQuestionOptions = (
    dq: QuestionMultiChoice | QuestionSingleChoice |
    QuestionMultiChoiceWithText | QuestionSingleChoiceWithText,
    cases: DigitalCase[],
  ) => {
    const { payload } = dq;
    if (!payload) {
      return null;
    }
    return payload.map((p: ChoiceQuestionPayload) => (
      <TableRow key={p.id}>
        <TableCell>{p.text}</TableCell>
        {cases.map((dc) => (
          <TableCell key={dc.id} />
        ))}
      </TableRow>
    ));
  };

  const renderMultipleChoiceTable = (
    dq: QuestionMultiChoice | QuestionSingleChoice |
    QuestionMultiChoiceWithText | QuestionSingleChoiceWithText,
    cases: DigitalCase[],
  ) => (
    <Table className="multiple-choice" size="small" sx={{ position: 'relative', overflow: 'auto' }}>
      <TableHead>
        <TableRow>
          <TableCell>-</TableCell>
          {cases.map((dc) => (
            <TableCell key={dc.id}>{dc.name}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>{renderRowsForQuestionOptions(dq, cases)}</TableBody>
    </Table>
  );

  const renderStringInputTable = (
    dq: QuestionString | QuestionRatio | QuestionNumberRange,
    cases: DigitalCase[],
  ) => (
    <Table size="small" sx={{ position: 'relative', overflow: 'auto' }}>
      <TableHead>
        <TableRow>
          {cases.map((dc) => (
            <TableCell key={dc.id}>{dc.name}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {cases.map((dc) => (
            <TableCell sx={{ p: '1em' }} key={`answer-${dc.id}`} />
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );

  const renderSpecificTypeTable = (dq: DigitalQuestion, cases: DigitalCase[]) => {
    switch (dq.type) {
      case QuestionTypeV2.MultiChoice:
      case QuestionTypeV2.SingleChoice:
      case QuestionTypeV2.SingleChoiceWithText:
      case QuestionTypeV2.MultiChoiceWithText:
        return renderMultipleChoiceTable(dq, cases);
      case QuestionTypeV2.String:
      case QuestionTypeV2.Ratio:
      case QuestionTypeV2.NumberRange:
        return renderStringInputTable(dq, cases);
      default:
        return null;
    }
  };

  const renderStringInputQuestion = (dq: QuestionString | QuestionRatio | QuestionNumberRange) => (
    <Box key={dq.id} sx={{ breakInside: 'avoid-page', mt: '0.8em', mb: '0.8em' }}>
      <Typography fontWeight={500} sx={{ fontSize: '0.8rem' }}>{dq.text}</Typography>
      <input type="text" style={{ width: '100%' }} />
    </Box>
  );

  const renderMultipleChoiceQuestion = (dq: QuestionMultiChoice | QuestionSingleChoice) => (
    <Box key={dq.id} sx={{ breakInside: 'avoid-page', mt: '0.8em', mb: '0.8em' }}>
      <Typography fontWeight={500} sx={{ fontSize: '0.8rem' }}>{dq.text}</Typography>
      <FormControl>
        {dq.payload?.map((p) => (
          <FormControlLabel
            key={p.id}
            control={<Radio sx={{ p: 0 }} />}
            sx={{
              margin: 0,
            }}
            label={
              <Typography sx={{ fontSize: '0.8rem' }}>{p.text}</Typography>
            }
          />
        ))}
      </FormControl>
    </Box>
  );

  const renderChoiceWithText = (dq: QuestionChoiceWithText) => (
    <Box key={dq.id} sx={{ breakInside: 'avoid-page', mt: '0.8em', mb: '0.8em' }}>
      <Typography fontWeight={500} sx={{ fontSize: '0.8rem' }}>{dq.text}</Typography>
      <FormControl>
        {dq.payload?.options?.map((p) => (
          <FormControlLabel
            key={p.id}
            control={<Radio sx={{ p: 0 }} />}
            sx={{
              margin: 0,
            }}
            label={(
              <Typography sx={{ fontSize: '0.8rem' }}>
                {p.text}
                {p.textBox ? ': __________________' : ''}
              </Typography>
            )}
          />
        ))}
      </FormControl>
    </Box>
  );

  const renderSingleOrMultiChoiceWithText = (
    dq: QuestionSingleChoiceWithText|QuestionMultiChoiceWithText,
  ) => (
    <Box key={dq.id} sx={{ breakInside: 'avoid-page', mt: '0.8em', mb: '0.8em' }}>
      <Typography fontWeight={500} sx={{ fontSize: '0.8rem' }}>{dq.text}</Typography>
      <FormControl>
        {dq.payload?.map((p) => (
          <FormControlLabel
            key={p.id}
            control={<Radio sx={{ p: 0 }} />}
            sx={{
              margin: 0,
            }}
            label={(
              <Typography sx={{ fontSize: '0.8rem' }}>
                {p.text}
                {p.textBox ? ': __________________' : ''}
              </Typography>
            )}
          />
        ))}
      </FormControl>
    </Box>
  );

  const renderSpecificContextQuestionType = (dq: DigitalQuestion) => {
    switch (dq.type) {
      case QuestionTypeV2.MultiChoice:
      case QuestionTypeV2.SingleChoice:
        return renderMultipleChoiceQuestion(dq);
      case QuestionTypeV2.ChoiceWithText:
        return renderChoiceWithText(dq);
      case QuestionTypeV2.SingleChoiceWithText:
      case QuestionTypeV2.MultiChoiceWithText:
        return renderSingleOrMultiChoiceWithText(dq);
      case QuestionTypeV2.String:
      case QuestionTypeV2.Ratio:
      case QuestionTypeV2.NumberRange:
        return renderStringInputQuestion(dq);
      default:
        return null;
    }
  };

  return (
    <SectionGroup ref={onRef}>
      <Box>
        {
          sections.map((sec) => (
            <SectionContainer key={sec.id}>
              <Typography variant="h6">{sec.name}</Typography>
              {
                // render main question
                getQuestions(sec.id).map((dq) => {
                  const fullCases = getCases(sec.id);
                  const chunkSize = 10;
                  const casesInChunks = [];
                  for (let i = 0; i < fullCases.length; i += chunkSize) {
                    casesInChunks.push(fullCases.slice(i, i + chunkSize));
                  }

                  return (
                    <Box key={dq.id} sx={{ my: 4 }}>
                      <Typography fontWeight={500} sx={{ fontSize: '0.8rem' }}>{dq.text}</Typography>
                      {casesInChunks.map((cases) => (
                        <TableContainer
                          key={`${dq.id}-${cases[0].id}`}
                          sx={{ overflow: 'unset', mt: 1 }}
                        >
                          {renderSpecificTypeTable(dq, cases)}
                        </TableContainer>
                      ))}
                    </Box>
                  );
                })
              }
              {
                // render context questions
                getContextQuestions(sec.id).map((dq) => renderSpecificContextQuestionType(dq))
              }
            </SectionContainer>
          ))
        }
      </Box>
    </SectionGroup>
  );
};

export default WholeSurveyPrint;
